import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useUpdateProductMutation, useDetailedProductQuery,
  useUpdateVariantMutation, useUploadProductImageMutation, useGetAllCategoriesQuery,
} from "../../services/productApi";
import { DriveFolderUpload, Inventory2Outlined } from "@mui/icons-material";
import "./productForm.css";
import {
  AdminPageHeader,
  AdminCard,
  AdminInput,
  AdminSelect,
  LoadingButton,
  AdminEmptyState,
  AdminSkeleton,
  useToast,
} from "../../components/ui";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000/";

function ProductEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [server_error, setServerError] = useState({});
  const [data, setData] = useState(null);
  const [updateProduct, { isLoading }] = useUpdateProductMutation();
  const [updateVariant] = useUpdateVariantMutation();
  const [uploadProductImage] = useUploadProductImageMutation();
  const { data: categories, isLoading: categoriesLoading } = useGetAllCategoriesQuery();

  const [imge, setImge] = useState({
    image1: '', image2: '', image3: '', image4: '',
  });

  const productRes = useDetailedProductQuery(id);

  useEffect(() => {
    if (productRes.isSuccess) {
      const variant = productRes.data.variants && productRes.data.variants[0];
      const images = productRes.data.images || [];
      setData({
        name: productRes.data.name,
        id: productRes.data.id,
        description: productRes.data.description,
        variantId: variant ? variant.id : null,
        price: variant ? variant.price : '',
        stock_quantity: variant ? variant.stock_quantity : '',
        category: productRes.data.category ? productRes.data.category.id : '',
        image1: images[0] ? `${API_BASE}${images[0].image}` : '',
        image2: images[1] ? `${API_BASE}${images[1].image}` : '',
        image3: images[2] ? `${API_BASE}${images[2].image}` : '',
        image4: images[3] ? `${API_BASE}${images[3].image}` : '',
      })
    }
  }, [productRes])

  if (productRes.isLoading || !data) {
    return (
      <div>
        <AdminPageHeader title="Edit Product" breadcrumbs={[{ label: "Dashboard", to: "/admin/dashboard" }, { label: "Products", to: "/admin/products" }, { label: "Edit" }]} />
        <AdminCard>
          <AdminSkeleton variant="text" width="30%" />
          <div style={{ marginTop: 16 }}><AdminSkeleton variant="card" height={200} /></div>
        </AdminCard>
      </div>
    );
  }

  if (productRes.isError) {
    return (
      <div>
        <AdminPageHeader title="Edit Product" breadcrumbs={[{ label: "Dashboard", to: "/admin/dashboard" }, { label: "Products", to: "/admin/products" }, { label: "Edit" }]} />
        <AdminEmptyState icon={<Inventory2Outlined />} title="Couldn't load this product" description="Something went wrong. Try refreshing the page." />
      </div>
    );
  }

  const handleChange = event => {
    const name = event.target.name;
    const value = event.target.value;
    setData(values => ({ ...values, [name]: value }));
  };

  const handleImage = event => {
    const name = event.target.name;
    const value = event.target.files[0];
    setImge(values => ({ ...values, [name]: value }));
    const objectUrl = URL.createObjectURL(value)
    setData(values => ({ ...values, [name]: objectUrl }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault()

    const res = await updateProduct({
      id,
      data: {
        name: data.name,
        description: data.description,
        category: data.category,
      },
    })

    if (res.error) {
      setServerError(res.error.data?.errors || res.error.data || {});
      toast.error("Could not update this product. Check the fields below.");
      return;
    }

    if (data.variantId) {
      await updateVariant({
        id: data.variantId,
        data: { price: data.price, stock_quantity: data.stock_quantity },
      })
    }

    let sortOrder = 0;
    for (const key of ['image1', 'image2', 'image3', 'image4']) {
      if (imge[key]) {
        const formData = new FormData();
        formData.append('image', imge[key]);
        formData.append('sort_order', sortOrder);
        await uploadProductImage({ productId: id, formData });
      }
      sortOrder++;
    }

    toast.success("Product updated successfully.");
    setTimeout(() => navigate('/admin/products'), 1200);
  }

  return (
    <div>
      <AdminPageHeader
        title={data.name || "Edit Product"}
        breadcrumbs={[
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Products", to: "/admin/products" },
          { label: "Edit" },
        ]}
      />
      <AdminCard>
        <form onSubmit={handleSubmit} className="productFormLayout">
          <div className="productFormLayout__photos">
            <div className="photoGrid">
              {['image1', 'image2', 'image3', 'image4'].map((key, i) => (
                <div className="productPhoto" key={key}>
                  <img src={data[key]} alt={`Product ${i + 1}`} />
                  <div className="uploadContainer">
                    <DriveFolderUpload sx={{ fontSize: "18px" }} />
                    <input type="file" name={key} onChange={handleImage} aria-label={`Upload photo ${i + 1}`} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="productFormLayout__fields">
            <AdminInput
              label="Product Name"
              name="name"
              value={data.name || ""}
              onChange={handleChange}
              error={server_error.name?.[0]}
              required
            />
            <AdminInput
              label="Product Description"
              name="description"
              value={data.description || ""}
              onChange={handleChange}
              error={server_error.description?.[0]}
            />
            <div className="productFormRow">
              <AdminInput label="Price" name="price" type="number" value={data.price || ""} onChange={handleChange} error={server_error.price?.[0]} required />
              <AdminInput label="Stock" name="stock_quantity" type="number" value={data.stock_quantity || ""} onChange={handleChange} error={server_error.stock_quantity?.[0]} required />
            </div>
            <AdminSelect
              label="Category"
              name="category"
              value={data.category || ""}
              onChange={handleChange}
              error={server_error.category?.[0]}
              required
              disabled={categoriesLoading}
            >
              <option value="" disabled>{categoriesLoading ? "Loading categories..." : "Select a category"}</option>
              {(categories || []).map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </AdminSelect>
            <div className="productFormActions">
              <LoadingButton type="submit" loading={isLoading} loadingLabel="Saving...">
                Update Product
              </LoadingButton>
            </div>
          </div>
        </form>
      </AdminCard>
    </div>
  );
}

export default ProductEdit;
